import React, { useState, useRef, useEffect } from 'react'
import { Container, Button, useMediaQuery, useTheme, CircularProgress } from '@mui/material'
import { useLocation } from 'react-router-dom'
import ArrowBackIcon from '../../icons/arrowBack'
import ArrowForwardIcon from '../../icons/arrowForward'
import { FormData, ContactInfo, SurveyState } from './types'
import CityStep from './steps/CityStep'
import MoveInStep from './steps/MoveInStep'
import UnitTypeStep from './steps/UnitTypeStep'
import BudgetStep from './steps/BudgetStep'
import ContactStep from './steps/ContactStep'
import surveyLogo from '../../assets/imgs/surveyLogo.svg'

const WechatSurvey: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()

  // Step refs for desktop scrolling
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])

  const [customerId, setCustomerId] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const [state, setState] = useState<SurveyState>({
    currentStep: 0,
    isSubmitted: false,
    errors: {},
    formData: {
      city: '',
      moveIn: '',
      unitType: { beds: [], baths: [] },
      budget: [0, 5000],
      contact: {
        wechat: '',
        phone: '',
        email: '',
      },
    },
  })

  // Get customer_id and token from URL and check if already submitted
  useEffect(() => {
    const fetchQuestionnaireDetail = async () => {
      try {
        // Get customer_id and token from URL params
        const params = new URLSearchParams(location.search)
        const id = params.get('customer_id')
        const authToken = params.get('token')
        if (id) {
          setCustomerId(id)
        }

        if (authToken) {
          setToken(authToken)
        }

        if (id && authToken) {
          const baseUrl = process.env.REACT_APP_CUSTOMER_BASE_URL || ''

          // Call detail API with token
          const response = await fetch(`${baseUrl}/api/questionaire/get/${id}`, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          })

          if (response.ok) {
            const data = await response.json()
            // If data exists, already submitted, show success page
            if (data?.data?.customer_id) {
              setState(prev => ({ ...prev, isSubmitted: true }))
            }
          }
          // If 404 or error, not submitted yet, allow filling
        }
      } catch (error) {
        console.error('Failed to fetch questionnaire detail:', error)
        // Allow filling on error
      } finally {
        setIsLoading(false)
      }
    }

    fetchQuestionnaireDetail()
  }, [location.search])

  // Validate step
  const validateStep = (index: number): boolean => {
    const { formData } = state

    switch (index) {
      case 0: // City
        return !!formData.city
      case 1: // Move-in date
        return !!formData.moveIn
      case 2: // Unit type
        return formData.unitType.beds.length > 0 || formData.unitType.baths.length > 0
      case 3: // Budget
        return formData.budget[0] >= 0 && formData.budget[1] > formData.budget[0]
      case 4: // Contact
        return !!(formData.contact.wechat || formData.contact.phone)
      default:
        return false
    }
  }

  // Validate all steps
  const validateAllSteps = (): boolean => {
    const newErrors: Record<number, boolean> = {}
    let isValid = true

    for (let i = 0; i < 5; i++) {
      if (!validateStep(i)) {
        newErrors[i] = true
        isValid = false
      }
    }

    setState(prev => ({ ...prev, errors: newErrors }))
    return isValid
  }

  // Mobile: Next step
  const handleNext = () => {
    const { currentStep } = state

    if (!validateStep(currentStep)) {
      setState(prev => ({
        ...prev,
        errors: { ...prev.errors, [currentStep]: true },
      }))
      return
    }

    // Clear current step error
    setState(prev => {
      const newErrors = { ...prev.errors }
      delete newErrors[currentStep]
      return {
        ...prev,
        currentStep: currentStep + 1,
        errors: newErrors,
      }
    })
  }

  // Mobile: Previous step
  const handleBack = () => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }))
  }

  // Desktop: Scroll to step
  const scrollToStep = (index: number) => {
    if (stepRefs.current[index]) {
      stepRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  // Desktop: Auto scroll to next step after selection
  const handleAutoAdvance = (currentIndex: number) => {
    if (!isMobile && currentIndex < 4) {
      setTimeout(() => {
        scrollToStep(currentIndex + 1)
      }, 500)
    }
  }

  // Submit form
  const handleSubmit = async () => {
    if (!validateAllSteps()) {
      // Find first error step
      const firstErrorStep = Object.keys(state.errors).map(Number).sort()[0]

      if (firstErrorStep !== undefined) {
        if (isMobile) {
          setState(prev => ({ ...prev, currentStep: firstErrorStep }))
        } else {
          scrollToStep(firstErrorStep)
        }
      }
      return
    }

    try {
      const { formData } = state

      // Parse unit type data: if multiple selected, take first; 4+ becomes 4, 3+ becomes 3
      const parseBdBa = (value: string): number => {
        if (value === '单身公寓(Studio)') return 0
        if (value.includes('+')) return parseInt(value.replace('+', ''), 10)
        return parseFloat(value)
      }

      const bd = formData.unitType.beds.length > 0 ? parseBdBa(formData.unitType.beds[0]) : 1
      const ba = formData.unitType.baths.length > 0 ? parseBdBa(formData.unitType.baths[0]) : 1

      // Build submit data
      const submitData = {
        customer_id: customerId ? parseInt(customerId, 10) : undefined,
        city: formData.city,
        move_in_date: formData.moveIn,
        bd: bd.toString(),
        ba: ba.toString(),
        budget_min: formData.budget[0],
        budget_max: formData.budget[1],
        wechat_no: formData.contact.wechat || undefined,
        phone: formData.contact.phone || undefined,
        email: formData.contact.email || undefined,
      }

      const baseUrl = process.env.REACT_APP_CUSTOMER_BASE_URL || ''

      // Call save API with token
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      const response = await fetch(`${baseUrl}/api/questionaire/save`, {
        method: 'POST',
        headers,
        body: JSON.stringify(submitData),
      })

      if (!response.ok) {
        throw new Error('提交失败')
      }

      // Submit success
      setState(prev => ({ ...prev, isSubmitted: true }))
    } catch (error) {
      console.error('Submit error:', error)
      alert('提交失败，请稍后重试')
    }
  }

  // Update form data
  const updateFormData = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setState(prev => {
      const newFormData = { ...prev.formData, [key]: value }
      const newErrors = { ...prev.errors }

      // Clear error state for this step
      const stepIndex = ['city', 'moveIn', 'unitType', 'budget', 'contact'].indexOf(key)
      if (stepIndex !== -1) {
        delete newErrors[stepIndex]
      }

      return {
        ...prev,
        formData: newFormData,
        errors: newErrors,
      }
    })
  }

  // Render step content
  const renderStep = (index: number) => {
    const { formData, errors } = state

    switch (index) {
      case 0:
        return (
          <CityStep
            value={formData.city}
            onChange={value => {
              updateFormData('city', value)
              handleAutoAdvance(0)
            }}
            error={errors[0]}
            token={token}
          />
        )
      case 1:
        return (
          <MoveInStep
            value={formData.moveIn}
            onChange={value => {
              updateFormData('moveIn', value)
              handleAutoAdvance(1)
            }}
            error={errors[1]}
          />
        )
      case 2:
        return <UnitTypeStep value={formData.unitType} onChange={value => updateFormData('unitType', value)} error={errors[2]} />
      case 3:
        return <BudgetStep value={formData.budget} onChange={value => updateFormData('budget', value)} error={errors[3]} />
      case 4:
        return (
          <ContactStep value={formData.contact} onChange={(value: ContactInfo) => updateFormData('contact', value)} error={errors[4]} />
        )
      default:
        return null
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center pt-20 px-4 min-h-screen bg-[#f7f9fc]">
        <CircularProgress size={48} sx={{ color: '#050a22' }} />
        <p className="mt-4 text-gray-600">加载中...</p>
      </div>
    )
  }

  // Success page
  if (state.isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center pt-20 px-4 min-h-screen bg-[#f7f9fc]">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-[#e0e2ef] max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-[#050a22] mb-2">提交成功！</h2>
          <p className="text-[#555] leading-relaxed">您的需求已收到，我们会尽快为您匹配合适的房源。</p>
        </div>
      </div>
    )
  }

  // Mobile layout
  if (isMobile) {
    const progress = ((state.currentStep + 1) / 5) * 100

    return (
      <div className="min-h-screen bg-[#f7f9fc] pb-20">
        {/* 头部 */}
        <div className="bg-[#111212] px-6 py-6 text-white shadow-md">
          <div className="flex items-center gap-3">
            <img src={surveyLogo} alt="Logo" className="h-6" />
            <div className="h-6 w-[1px] bg-white/20" />
            <h1 className="text-lg font-semibold tracking-wide">租房需求问卷</h1>
          </div>
        </div>

        {/* 进度条 */}
        <div className="px-4 pt-6 pb-4">
          <div className="mb-1 h-1 w-full rounded-full bg-gray-200">
            <div className="h-full rounded-full bg-[#050a22] transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-2 text-sm font-medium text-gray-500">问题 {state.currentStep + 1} / 5</p>
        </div>

        {/* 内容 */}
        <div className="px-4">
          <div className="rounded-2xl bg-white p-6 shadow-sm border border-[#e0e2ef]">{renderStep(state.currentStep)}</div>
        </div>

        {/* 底部按钮 */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#e0e2ef] p-4">
          <div className="flex gap-3">
            {state.currentStep > 0 && (
              <Button
                variant="outlined"
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                fullWidth
                sx={{
                  borderRadius: '12px',
                  py: 1.5,
                  fontSize: '1rem',
                  borderColor: '#e0e2ef',
                  color: '#050a22',
                  '&:hover': {
                    borderColor: '#050a22',
                    backgroundColor: 'rgba(5, 10, 34, 0.04)',
                  },
                }}
              >
                上一步
              </Button>
            )}
            {state.currentStep < 4 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
                fullWidth
                sx={{
                  borderRadius: '12px',
                  py: 1.5,
                  fontSize: '1rem',
                  backgroundColor: '#050a22',
                  '&:hover': {
                    backgroundColor: 'rgba(5, 10, 34, 0.9)',
                  },
                }}
              >
                下一步
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                fullWidth
                sx={{
                  borderRadius: '12px',
                  py: 1.5,
                  fontSize: '1rem',
                  backgroundColor: '#e74f4f',
                  '&:hover': {
                    backgroundColor: '#d64444',
                  },
                }}
              >
                提交
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Desktop layout
  return (
    <div className="min-h-screen bg-[#f7f9fc]">
      {/* 头部 */}
      <div className="bg-[#111212] px-6 py-6 text-white shadow-md mb-10">
        <div className="mx-auto max-w-2xl flex items-center gap-3">
          <img src={surveyLogo} alt="Logo" className="h-6" />
          <div className="h-6 w-[1px] bg-white/20" />
          <h1 className="text-lg font-semibold tracking-wide">租房需求问卷</h1>
        </div>
      </div>

      <Container maxWidth="md" sx={{ px: 4 }}>
        {/* 提示信息 */}
        <div className="mb-10 flex items-start gap-4 rounded-lg bg-[#f7f9fc] p-0">
          <div className="flex gap-2 items-start p-4 bg-white rounded-lg shadow-sm border border-[#e0e2ef] w-full">
            <div className="mt-0.5 shrink-0 p-0.5 rounded text-black">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-sm text-[#050a22] leading-5">请回答以下5个问题，以便我们为您匹配最合适的房源。</p>
          </div>
        </div>

        {/* 所有步骤 */}
        <div className="flex flex-col gap-12 bg-white p-12 rounded-2xl shadow-sm border border-[#e0e2ef]">
          {[0, 1, 2, 3, 4].map(index => (
            <div
              key={index}
              id={`step-${index}`}
              className="border-b border-dashed border-gray-200 pb-12 last:border-0 last:pb-0"
              ref={el => (stepRefs.current[index] = el)}
            >
              {renderStep(index)}
            </div>
          ))}

          <div className="pt-4 flex justify-start">
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                minWidth: '140px',
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: '#e74f4f',
                boxShadow: '0 10px 15px -3px rgba(231, 79, 79, 0.1), 0 4px 6px -4px rgba(231, 79, 79, 0.1)',
                '&:hover': {
                  backgroundColor: '#d64444',
                },
              }}
            >
              提交
            </Button>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default WechatSurvey
